import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { booleanDataSocket, categoricalDataSocket, numericDataSocket } from "../socket_types"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../tile_grid"
import { BaseComponent } from "./base_component"
import {
  mergeLabelOutputWireSnapshotsForReconnect,
  reconnectLabelOutputsByNormalizedLabel,
  runWithLabelOutputReconnect,
  snapshotLabelOutputConnections,
  takePendingLabelOutputWireSnapshots,
} from "./categorical_dynamic_io_connections"
import { scheduleDownstreamDynamicLabelOutputRefresh } from "./categorical_label_chain"
import { SelectControl } from "../controls/select"
import { CompiledDatasetRecord, DatasetLabelRecord, getDataset, getDatasetLabels } from "../../state/saved_dataset"
import { Extent } from "ol/extent"
import { createXYZ } from "ol/tilegrid"
import { maskFromExtentAndShape } from "../bounding_box"
import { ProjectProperties } from "."

async function fetchDataset(datasetId: number, teamId: number) {
    return new Promise<{ error: { message: string }; out: { model: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid } }>((resolve) => {
        getDataset(datasetId, teamId, (err, out, colorMetadata) => {
            // colorMetadata is not used in this context (model editor), only in map view
            resolve({ error: err, out: out });
        });
    });
}

export type getDatasets = () => Promise<CompiledDatasetRecord[]>

export class PrecompiledModelComponent extends BaseComponent {
    modelSource: getDatasets
    models: CompiledDatasetRecord[]
    projectExtent: Extent
    projectZoom: number
    maskMode: boolean
    maskLayer: string
    maskCQL: string
    projectProps: ProjectProperties
    labelsCache: Record<number, DatasetLabelRecord[]>
    constructor(getDatasets: getDatasets, projectProps: ProjectProperties) {
        super("Load Dataset")
        this.category = "Inputs"
        this.modelSource = getDatasets
        this.projectExtent = projectProps.extent
        this.projectZoom = projectProps.zoom
        this.maskMode = projectProps.mask
        this.maskLayer = projectProps.maskLayer
        this.maskCQL = projectProps.maskCQL
        this.projectProps = projectProps
        this.labelsCache = {}
    }

    private outputKeyForLabel(value: number): string {
        return `label_${value}`
    }

    private syncOutputs(
        node: Node,
        mainSocket: typeof booleanDataSocket | typeof categoricalDataSocket | typeof numericDataSocket,
        labels: DatasetLabelRecord[]
    ) {
        const pending = takePendingLabelOutputWireSnapshots(node)
        const live =
            this.editor != null ? snapshotLabelOutputConnections(node, k => k.startsWith("label_")) : []
        const snaps = mergeLabelOutputWireSnapshotsForReconnect(live, pending)

        const desired = new Map<string, { name: string; socket: any }>()
        desired.set("out", { name: "Model", socket: mainSocket })
        labels.forEach((entry) => {
            desired.set(this.outputKeyForLabel(entry.value), { name: entry.label, socket: booleanDataSocket })
        })

        const existing = Array.from(node.outputs.values())
        existing.forEach((out) => {
            const want = desired.get(out.key)
            if (!want) {
                ;[...out.connections].forEach(c => this.editor?.removeConnection(c))
                node.removeOutput(out)
                return
            }
            if (out.socket !== want.socket) {
                ;[...out.connections].forEach(c => this.editor?.removeConnection(c))
                node.removeOutput(out)
                node.addOutput(new Output(out.key, want.name, want.socket))
            } else if (out.name !== want.name) {
                out.name = want.name
            }
            desired.delete(out.key)
        })

        desired.forEach((want, key) => {
            node.addOutput(new Output(key, want.name, want.socket))
        })

        if (this.editor) {
            runWithLabelOutputReconnect(node, () =>
                reconnectLabelOutputsByNormalizedLabel(this.editor!, node, snaps, k => k.startsWith("label_"))
            )
        }
    }

    async builder(node: Node) {

        this.models = await this.modelSource()
        this.models.sort((a, b) => a.name.localeCompare(b.name))

        node.meta.toolTip = "Load a precompiled dataset. These can be created from the 'Save Dataset' component."

        node.addControl(new SelectControl(
            this.editor,
            'DatasetId',
            () => this.models,
            () => this.updateOutputs(node),
            'Dataset'
        ))

        await this.updateOutputs(node)
    }

    async updateOutputs(node: Node) {

        let output = this.models.find(a => a.id == node.data.DatasetId as number)

        if (!output && this.models.length > 0) {
            output = this.models[0]
            node.data.DatasetId = output.id
        }

        if (!output) {
            delete (node.data as Record<string, unknown>)._datasetLabelsSourceId
            this.syncOutputs(node, numericDataSocket, [])
            node.update()
            scheduleDownstreamDynamicLabelOutputRefresh(this.editor, node, "out")
            return
        }

        const mainSocket =
            output.gridtype === 'BooleanTileGrid'
                ? booleanDataSocket
                : (output.gridtype === 'CategoricalTileGrid' ? categoricalDataSocket : numericDataSocket)

        if (output.gridtype === 'CategoricalTileGrid') {
            const labelsForDatasetId = node.data._datasetLabelsSourceId as number | undefined
            if (
                labelsForDatasetId !== undefined &&
                labelsForDatasetId !== output.id &&
                Array.isArray(node.data.datasetLabels)
            ) {
                delete node.data.datasetLabels
            }

            const savedLabels = Array.isArray(node.data.datasetLabels)
                ? node.data.datasetLabels as DatasetLabelRecord[]
                : []
            const cachedLabels = this.labelsCache[output.id] || []
            // Prefer in-memory cache for this dataset id so switching categorical datasets
            // does not briefly rebuild sockets from stale saved labels (which breaks name-based reconnect).
            const immediateLabels = cachedLabels.length > 0 ? cachedLabels : savedLabels

            if (immediateLabels.length > 0) {
                this.syncOutputs(node, mainSocket, immediateLabels)
            }

            if (cachedLabels.length === 0) {
                try {
                    const fetched = await getDatasetLabels(output.id, output.team_id)
                    this.labelsCache[output.id] = fetched
                    node.data.datasetLabels = fetched
                    ;(node.data as Record<string, unknown>)._datasetLabelsSourceId = output.id
                    this.syncOutputs(node, mainSocket, fetched)
                } catch (_e) {
                    // Keep existing labels if fetch fails.
                }
            } else {
                node.data.datasetLabels = cachedLabels
                ;(node.data as Record<string, unknown>)._datasetLabelsSourceId = output.id
            }
        } else {
            delete node.data.datasetLabels
            delete (node.data as Record<string, unknown>)._datasetLabelsSourceId
            this.syncOutputs(node, mainSocket, [])
        }

        node.update()
        scheduleDownstreamDynamicLabelOutputRefresh(this.editor, node, "out")
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const editorNode = this.editor?.nodes.find((n) => n.id === node.id);
        if (editorNode === undefined) {
            return;
        }

        let dataId = node.data.DatasetId ? (node.data.DatasetId as number) : this.models[0].id;

        const dataset = this.models.find((a) => a.id == dataId);

        delete editorNode.meta.errorMessage;

        if (dataset) {

            const mask = await maskFromExtentAndShape(this.projectProps)

            try {
                const response = await fetchDataset(dataset.id, dataset.team_id);
                if (response.error) {
                    editorNode.meta.errorMessage = response.error.message;
                } else {
                    const model = response.out;
                    
                    const tileGrid = createXYZ()
                    const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectExtent, this.projectZoom)

                    if (model instanceof BooleanTileGrid) {
                        const out = outputs['out'] = editorNode.meta.output = new BooleanTileGrid(this.projectZoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight())
                        out.iterate((x, y, v) => out.set(x, y, mask.get(x, y) === true ? model.get(x, y, this.projectZoom) : false))
                    } else if (model instanceof CategoricalTileGrid) {
                        const out = outputs['out'] = editorNode.meta.output = new CategoricalTileGrid(this.projectZoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight(), undefined, model.labels)
                        out.iterate((x, y, v) => out.set(x, y, mask.get(x, y) === true ? model.get(x, y, this.projectZoom) : 255))

                        const labels = ((editorNode.data.datasetLabels as DatasetLabelRecord[] | undefined) || [])
                            .concat(
                                Array.from(model.labels?.entries?.() || []).map(([value, label]) => ({ value: Number(value), label: String(label) }))
                            )
                            .filter((entry, idx, arr) => arr.findIndex(e => e.value === entry.value) === idx)
                        labels.forEach((entry) => {
                            const boolOut = new BooleanTileGrid(this.projectZoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight(), false)
                            boolOut.iterate((x, y) => {
                                if (mask.get(x, y) === true && out.get(x, y, this.projectZoom) === entry.value) {
                                    boolOut.set(x, y, true)
                                }
                            })
                            boolOut.name = entry.label
                            outputs[this.outputKeyForLabel(entry.value)] = boolOut
                        })
                    } else if (model instanceof NumericTileGrid) {
                        const out = new NumericTileGrid(this.projectZoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight(), NaN)
                        out.iterate((x, y, v) => out.set(x, y, mask.get(x, y) === true ? model.get(x, y, this.projectZoom) : NaN))

                        if(out.getMinMax()[1] === -Infinity) {
                            editorNode.meta.errorMessage = "No valid data found in dataset. Possible cause: no coverage for selected area."
                            outputs['out'] = editorNode.meta.output = undefined
                        }else{
                            outputs['out'] = editorNode.meta.output = out
                        }
                    }

                }
            } catch (error) {
                editorNode.meta.errorMessage = error.message;
            }
        } else {
            editorNode.meta.errorMessage = "Unable to retrieve valid model data";
        }

        editorNode.update();

    }

}